import React, { useState } from 'react'
import { MenuItem, Select as MuiSelect, Checkbox, ListItemText } from '@mui/material'
import { t } from '../../../../infrastructure/i18nextHelper'
import { CustomSelectWrapper, CustomSelectProps } from './customSelectWrapper'
import { CustomSelectStyleProps, parseClassNames } from './select'
import { Item } from './types'
import { Filter } from '../../useFilter'

type GroupedMultiSelectProps = {
    values?: string[] | string | null | undefined
    choices?: Item[]
    allWhenEmpty?: boolean
    singleChoice?: boolean
    onChange?: (values: string[] | string) => void
    disabled?: boolean
    filter?: Filter
} & CustomSelectProps

export function GroupedMultiSelect(props: GroupedMultiSelectProps & CustomSelectStyleProps) {
    let [isEmpty, setIsEmpty] = useState<boolean>(false)
    const separator = '♣'

    let choices = (props.choices ?? props.filter?.choices ?? []) as Item[]
    let selected = props.values ?? props.filter?.selectedChoices

    let groups: [string, Item[]][] =
        Object.entries(choices.indexMultipleValueByProp('group')).sort((p1, p2) => p1[0] > p2[0] ? 1 : -1)

    let values: string | string[] = props.singleChoice
        ? (Array.isArray(selected) ? selected[0] : selected) ?? ''
        : (!isEmpty && selected && Array.isArray(selected) ? selected.filter(x => choices.some(s => s.value === x)) : [])

    let groupsFilled = groups.filter(x => x[1].every(y => values.indexOf(y.value) > -1))
    
    return (
        <CustomSelectWrapper label={props.label} classNames={parseClassNames({ ...props, choices: choices })}>
            <MuiSelect
                multiple={!props.singleChoice}
                id={props.id ?? ''}
                className={props.classesOverride?.select ?? props.classes.defaultSelect}
                value={values}
                displayEmpty={true}
                disabled={props.disabled}
                MenuProps={{ className: props.classes.noDrag }}
                renderValue={x => replaceByGroups(x as string[] | string)}
                onChange={e => onChange(e.target.value as string | string[])}>
                {groups.map((choicesByGroup, index) => {
                    let [group, choices] = choicesByGroup
                    return GroupLine(group, choices, index, !props.singleChoice)
                }).flat()}
            </MuiSelect>
        </CustomSelectWrapper>
    )

    function GroupLine(group: string, choices: Item[], key: number, multiple: boolean): JSX.Element[] {
        let result: JSX.Element[] = []
        let isSingleChoice = choices.length === 1 && choices[0].text === group

        if (!isSingleChoice) {
            let groupLine =
                <MenuItem disabled={!multiple} className={props.classes.itemWithCheckBox} key={group + key} value={getGroupValue(choices)}
                    classes={groupsFilled.some(x => x[0] === group) && multiple ? { root: props.classes.selected } : {}}>
                    {multiple && <Checkbox checked={choices.some(x => values.indexOf(x.value) > -1)} indeterminate={!choices.every(x => values.indexOf(x.value) > -1)} />}
                    <ListItemText primary={group} classes={props.classes ? { primary: props.classes?.selectGroup } : {}} />
                </MenuItem>

            let choiceLines = choices.map((choice, index) => (
                <MenuItem className={props.classes.menuItem} key={choice.value + index} value={choice.value}>
                    <span>     </span>{multiple && <Checkbox checked={!!(values?.indexOf(choice.value) > -1)} />}
                    <ListItemText primary={choice.text} />
                </MenuItem>))

            result = [groupLine, choiceLines].flat()
        }
        else {
            let singleLine =
                <MenuItem className={props.classes.itemWithCheckBox} key={group + key} value={choices[0].value}>
                    {multiple && <Checkbox checked={values?.indexOf(choices[0].value) > -1} />}
                    <ListItemText primary={group} classes={props.classes ? { primary: props.classes?.selectGroup } : {}} />
                </MenuItem>

            result = [singleLine]
        }

        return result
    }

    function replaceByGroups(selectedVals: string[] | string) {
        if (props.singleChoice) return choices.find(x => x.value === selectedVals as string)?.text

        let hasNoValue = !selectedVals || selectedVals.length === 0
        let hasAllValues = selectedVals.length === choices.length && choices.length > 2
        return hasNoValue || hasAllValues
            ? t('components.allFiltersLine')
            : (selectedVals as string[]).map(t => {
                let groupFound = groupsFilled.find(g => g[1].find(choice => choice.value === t))
                return groupFound && groupFound[1].length > 1 ? groupFound[0] : choices.find(x => x.value === t)?.text ?? t
            }).distinct().join(', ')
    }

    function getGroupValue(choices) { return choices.length > 1 ? choices.map(x => x.value).join(separator) : choices[0].value + separator }

    function onChange(selectedVals: string[] | string) {
        if (props.singleChoice) {
            if (props.onChange)
                props.onChange(selectedVals)
            else if (props.filter)
                props.filter.setSelectedChoices([selectedVals as string])
            return
        }
        let vals = (selectedVals as string[])?.map(x => x.split(separator)).flat().filter(x => x != '').distinct()
        let groupClicked = (selectedVals as string[]).find(x => x.contains(separator))?.split(separator).filter(x => x != '')
        if (groupClicked && groupClicked.every(x => (values ?? []).indexOf(x) > -1)) {
            vals = vals.filter(x => groupClicked!.indexOf(x) === -1)
        }

        if (vals.length === 0) {
            setIsEmpty(true)
            if (props.allWhenEmpty)
                vals = choices.map(x => x.value)
        }
        else if (isEmpty && vals.length > 0)
            setIsEmpty(false)

        if (props.onChange)
            props.onChange(vals)
        else if (props.filter)
            props.filter.setSelectedChoices(vals)
    }
}